import React, { useState, useEffect } from 'react';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { OpDrawer } from 'components/customAntd/DLS/OpDrawer/OpDrawer';
import { useSelector } from 'react-redux';
import VisitorsTab from './tabs/VisitorsTab';
import DetailsTab from './tabs/DetailsTab';
import { RootState } from 'store/store';
import { DRAWER_WIDTH } from 'constants/ui';
import { hasPermission } from 'utils/utils';
import VisitorsDrawerFooter from './footer/VisitorsDrawerFooter';
import VisitorsDrawerHeader from './header/VisitorsDrawerHeader';
import NotesTab from './tabs/NotesTab';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import GuestPassTab from './tabs/GuestPassTab';
import GuestPassLogTab from './tabs/GuestPassLogTab';
import { useTranslation } from 'react-i18next';

interface VisitorsDrawerProps {
	open: boolean;
	isProfileDrawerOpen: boolean;
	onClose: () => void;
	setIsProfileDrawerOpen: (open: boolean) => void;
	tab?: string;
}

const VisitorsDrawer: React.FC<VisitorsDrawerProps> = ({ open, isProfileDrawerOpen, onClose, setIsProfileDrawerOpen, tab }) => {
	const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
	const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
	const isGuestPassIntegrated = useSelector((state: RootState) => state.integration.isGuestPassIntegrated);
	const isTokenValid = useSelector((state: RootState) => state.integration.isTokenValid);

	const [activeKey, setActiveKey] = useState<string>('visitors');
	const [loading, setLoading] = useState<boolean>(false);
	const hasVisitorlogRead = hasPermission(tokenScopeList, orgId, 'o', 'visitorlog:r');
	const hasGuestpassRead = hasPermission(tokenScopeList, orgId, 'o', 'guestpass:r');

	const { t } = useTranslation();

	// Reset the active tab to 'visitors' when the drawer opens
	useEffect(() => {
		if (open) {
			setActiveKey(tab || 'visitors');
		}
	}, [tab, open]);

	const tabItems = [
		{
			key: 'visitors',
			label: t('visitors.tabs.visitors'),
			children: <VisitorsTab isProfileDrawerOpen={isProfileDrawerOpen} setIsProfileDrawerOpen={setIsProfileDrawerOpen} setDrawerLoading={setLoading} />
		},
		{
			key: 'details',
			label: t('visitors.tabs.details'),
			children: <DetailsTab />
		},
		...(hasVisitorlogRead ? [{
			key: 'notes',
			label: t('visitors.tabs.notes'),
			children: <NotesTab isActive={activeKey === 'notes'} />,
		}] : []),
		...(hasGuestpassRead && isGuestPassIntegrated && isTokenValid ? [{
			key: 'guestPass',
			label: t('visitors.tabs.guestPass'),
			children: <GuestPassTab />
		}] : []),
		...(hasGuestpassRead && isGuestPassIntegrated && isTokenValid ? [{
			key: 'guestPassLog',
			label: t('visitors.tabs.guestPassLog'),
			children: <GuestPassLogTab />
		}] : [])
	];

	return (
		<OpDrawer
			title={t('visitors.title')}
			width={DRAWER_WIDTH}
			open={open}
			onClose={onClose}
			extra={<VisitorsDrawerHeader onClose={onClose} setLoading={setLoading} loading={loading} />}
			footer={<VisitorsDrawerFooter setLoading={setLoading} loading={loading} />}
		>
			{loading ? (
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
					<Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
				</div>
			) : (
				<OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
			)}
		</OpDrawer >
	);
};

export default VisitorsDrawer;
